import { AuthProvider } from '@/contexts/AuthProvider';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { UsersProvider } from '@/contexts/UsersContext';
import { useAuth } from '@/hooks/useAuth';
import type { ReactNode } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { FeedbackWidget } from './feedback/FeedbackWidget';
import DashboardLayout from './page/DashboardLayout';

/**
 * Composant Layout principal qui gère :
 * - Tous les providers/contexts
 * - La logique de layout conditionnelle
 * - L'état global de l'application
 */

interface LayoutProps {
  children: ReactNode;
}

function LayoutContent({ children }: LayoutProps) {
  const { user } = useAuth();
  const location = useLocation();

  // Pages qui n'ont pas besoin du DashboardLayout (pages d'auth, pages publiques, etc.)
  const pageUrlsWithoutDashboard = [
    '/login',
    '/forgot-password',
    '/reset-password',
    '/auth/callback',
  ];

  // Vérifier si c'est une page publique (commence par /public/)
  const isPublicPage = location.pathname.startsWith('/public/');

  const shouldShowDashboard =
    user && !isPublicPage && !pageUrlsWithoutDashboard.includes(location.pathname);

  return shouldShowDashboard ? (
    <>
      <DashboardLayout>{children}</DashboardLayout>
      <FeedbackWidget />
    </>
  ) : (
    <div className="min-h-screen bg-white">{children}</div>
  );
}

export function Layout({ children }: LayoutProps) {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UsersProvider>
          <NavigationProvider>
            <LayoutContent>{children}</LayoutContent>
          </NavigationProvider>
        </UsersProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
